import { useEffect } from 'react'
import Router from 'next/router'
import { logger } from '@/utils/logging'
import { getIntegerFromSessionStorage, writeToSessionStorage } from '@/utils/session-storage/session-storage'
import { SessionStorageIntegerKey } from '@/utils/session-storage/session-storage-keys'

const CHUNK_ERROR_RELOAD_KEY: SessionStorageIntegerKey = 'chunkErrorReloadTimestamp'
const RELOAD_COOLDOWN_MS = 10000 // 10 seconds cooldown between reloads

/**
 * Checks if an error is a ChunkLoadError (webpack chunk loading failure).
 * These errors typically occur after a deployment when cached pages
 * try to load chunks that no longer exist.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false

  // Check for webpack ChunkLoadError
  if ('name' in error && typeof error.name === 'string' && error.name === 'ChunkLoadError') return true

  // Check for common chunk load error patterns in the message
  const message = 'message' in error && typeof error.message === 'string' ? error.message : ''
  if (
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    message.includes('ChunkLoadError')
  ) {
    return true
  }

  // Check for dynamic import failures
  if (message.includes('Failed to fetch dynamically imported module')) return true

  // Check for script loading failures that look like chunk errors
  if (message.includes('Failed to load script') && message.includes('.js')) return true

  return false
}

/**
 * Attempts to reload the page to recover from a chunk load error.
 * Includes protection against reload loops.
 * Returns true if reload was triggered, false if it was blocked.
 */
export function attemptChunkErrorReload(error: unknown): boolean {
  if (typeof window === 'undefined') return false

  // Check if we recently reloaded for a chunk error
  const lastReload = getIntegerFromSessionStorage(CHUNK_ERROR_RELOAD_KEY)
  const now = Date.now()

  if (lastReload) {
    const timeSinceLastReload = now - lastReload
    if (timeSinceLastReload < RELOAD_COOLDOWN_MS) {
      // We already tried reloading recently, don't loop
      logger().warn('Chunk error reload blocked - already attempted recently', { timeSinceLastReload }, error)
      return false
    }
  }

  // Log the error before reloading
  logger().info('Reloading page due to chunk load error', { url: window.location.href }, error)

  // Mark that we're reloading
  writeToSessionStorage(CHUNK_ERROR_RELOAD_KEY, now)

  // Force a hard reload to get fresh chunks
  window.location.reload()
  return true
}

/**
 * Hook that sets up global error handlers for chunk load errors.
 * When a chunk fails to load (typically after a deployment), the page
 * will automatically reload to fetch the new chunks.
 *
 * This should be called once in _app.tsx.
 */
export function useChunkErrorHandler(): void {
  useEffect(() => {
    // Handler for unhandled errors (catches synchronous chunk errors)
    const handleError = (event: ErrorEvent) => {
      if (isChunkLoadError(event.error)) {
        event.preventDefault()
        attemptChunkErrorReload(event.error)
      }
    }

    // Handler for unhandled promise rejections (catches async chunk errors)
    const handleRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) {
        event.preventDefault()
        attemptChunkErrorReload(event.reason)
      }
    }

    // Handler for Next.js router errors
    const handleRouteError = (err: Error) => {
      if (isChunkLoadError(err)) {
        attemptChunkErrorReload(err)
      }
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleRejection)
    Router.events.on('routeChangeError', handleRouteError)

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleRejection)
      Router.events.off('routeChangeError', handleRouteError)
    }
  }, [])
}
